import { Directive, effect, inject, input, SimpleChange } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { Tooltip } from 'primeng/tooltip';
import { map } from 'rxjs';

/**
 * A tooltip whose text is a translation key or plain text.
 *
 *   <button shTooltip="shell.peek.expand" tooltipPosition="bottom"></button>
 *   <a [shTooltip]="item.label" tooltipPosition="right" [tooltipDisabled]="expanded"></a>
 *
 * `tooltipPosition` ('top' | 'bottom' | 'left' | 'right') and
 * `tooltipDisabled` go straight to PrimeNG's tooltip (a host directive).
 * An empty text shows no tooltip.
 */
@Directive({
  selector: '[shTooltip]',
  standalone: true,
  hostDirectives: [
    {
      directive: Tooltip,
      inputs: ['tooltipPosition', 'tooltipDisabled'],
    },
  ],
})
export class ShTooltipDirective {
  /** Translation key or plain text. */
  readonly shTooltip = input<string | null | undefined>();

  private readonly _tooltip = inject(Tooltip);
  private readonly _translate = inject(TranslateService);
  private readonly _lang = toSignal(
    this._translate.onLangChange.pipe(map((e) => e.lang)),
    { initialValue: this._translate.currentLang }
  );

  constructor() {
    effect(() => {
      this._lang();
      const key = this.shTooltip();
      const text = key ? this._translate.instant(key) : '';
      const previous = this._tooltip.content;
      if (text === previous) return;
      this._tooltip.content = text;
      // PrimeNG reads its text through ngOnChanges (the `pTooltip` input).
      this._tooltip.ngOnChanges({
        content: new SimpleChange(previous, text, previous === undefined),
      });
    });
  }
}
